import { isConnectSplashType } from "./connectSplashConfig";
import { isJoinInviteType } from "./joinInviteConfig";
import { prepareItemWithServices } from "../pages/Manage/Admin/adminUtils";

export const DRAFT_PAYLOAD_FIELD = "draftPayload";
export const PUBLISHED_AT_FIELD = "publishedAt";
export const DRAFT_ENVELOPE_VERSION = 1;

/** Empty after PR 4 picker (including Header) shipped. Kept as an extension point. */
export const DRAFT_UNSAFE_UNTIL_PICKER = [];

export function canSaveDraftForType(type) {
  const t = String(type || "")
    .trim()
    .toLowerCase();
  return !DRAFT_UNSAFE_UNTIL_PICKER.some(
    (unsafe) => unsafe.toLowerCase() === t,
  );
}

export const DRAFT_OMIT_KEYS = [
  "_id",
  "__v",
  "_storeKey",
  "_localId",
  "draftPayload",
  "publishedAt",
  "createdAt",
  "updatedAt",
];

export function typeUsesPackedText(type) {
  return isConnectSplashType(type) || isJoinInviteType(type);
}

function clonePlain(value) {
  if (value == null || typeof value !== "object") return {};
  return { ...value };
}

export function packDraftEnvelope({
  section,
  type,
  values,
  updatedAt = new Date(),
} = {}) {
  const next = clonePlain(values);
  DRAFT_OMIT_KEYS.forEach((key) => {
    delete next[key];
  });
  if (typeUsesPackedText(type || next.type)) {
    delete next.text;
  }
  const at =
    updatedAt instanceof Date
      ? updatedAt.toISOString()
      : updatedAt
        ? String(updatedAt)
        : new Date().toISOString();
  return JSON.stringify({
    v: DRAFT_ENVELOPE_VERSION,
    updatedAt: at,
    section: section || "",
    type: type || next.type || "",
    values: next,
  });
}

export function unpackDraftEnvelope(raw) {
  if (raw == null || raw === "") return null;
  if (typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    if (parsed.v !== DRAFT_ENVELOPE_VERSION) return null;
    if (!parsed.values || typeof parsed.values !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

export function itemHasDraft(item) {
  if (!item || typeof item !== "object") return false;
  return Boolean(unpackDraftEnvelope(item.draftPayload));
}

export function isNeverPublished(item) {
  if (!item || typeof item !== "object") return false;
  const publishedAt = item.publishedAt;
  const hasPublishedAt =
    publishedAt != null && String(publishedAt).trim() !== "";
  return !hasPublishedAt && itemHasDraft(item);
}

/** True when the editor can restore a published snapshot (not a never-published stub). */
export function canRevertToPublished(item, { dirty = false } = {}) {
  if (!item || typeof item !== "object") return false;
  if (isNeverPublished(item)) return false;
  return itemHasDraft(item) || Boolean(dirty);
}

/**
 * Confirm dialog copy for Publish. Publish writes the snapshot; Active is what
 * visitors actually see. `message` is an array of paragraphs.
 */
export function publishConfirmCopy({
  visitorsWillSeeIt,
  otherActiveSameType = false,
  mode = "edit",
} = {}) {
  const snapshot =
    mode === "create"
      ? "This writes a published snapshot for this item."
      : "This replaces the published snapshot with the copy in this form.";
  const activeLine = visitorsWillSeeIt
    ? "Active is on, so visitors will see this version on the site."
    : "Active is off, so visitors will not see this version until you check Active.";
  const independent =
    "Publish writes the copy. Active is what shows or hides it on the page.";
  const paragraphs = [snapshot, activeLine, independent];
  if (otherActiveSameType) {
    paragraphs.push(
      "Another item of this type is already Active — visitors see the first active one. This does not auto-deactivate the other.",
    );
  }
  return {
    title: "Publish this copy?",
    message: paragraphs,
    confirmLabel: "Publish",
  };
}

export function schemaSupportsDraft(model) {
  return Boolean(
    model && Object.prototype.hasOwnProperty.call(model, "draftPayload"),
  );
}

export function stripDraftFields(item) {
  const next = clonePlain(item);
  delete next.draftPayload;
  delete next.publishedAt;
  return next;
}

export function formValuesFromItem(item, services = []) {
  if (!item || typeof item !== "object") {
    return prepareItemWithServices({}, services);
  }
  const envelope = unpackDraftEnvelope(item.draftPayload);
  const source = envelope ? envelope.values : item;
  return prepareItemWithServices(source, services);
}

export function publishedValuesFromItem(item, services = []) {
  if (!item || typeof item !== "object") {
    return prepareItemWithServices({}, services);
  }
  return prepareItemWithServices(stripDraftFields(item), services);
}

export function detectDraftFieldStripped(sentEnvelope, savedDoc) {
  const sent =
    typeof sentEnvelope === "string"
      ? sentEnvelope
      : sentEnvelope && sentEnvelope.draftPayload;
  if (!sent || String(sent).trim() === "") return false;
  if (!savedDoc || typeof savedDoc !== "object") return true;
  const saved = savedDoc.draftPayload;
  return saved == null || String(saved).trim() === "";
}

export function serializeDraftForSave(formValues, { section, type } = {}) {
  return {
    draftPayload: packDraftEnvelope({
      section,
      type: type || formValues?.type,
      values: formValues,
    }),
  };
}

export function adminGatherUser(user) {
  if (user && user.token && user.isAdmin === true) return user;
  return false;
}
